// A grade horária desenhada (VF-3) — pura.
//
// ★ A CORREÇÃO QUE ESTA PEÇA CARREGA.
//
// Na VF-2 a grade de sete colunas foi descartada com o argumento de que "linha
// vazia leria como livre". O argumento nasceu da premissa errada de que a
// agenda espelhava o Careflow — se ela só refletisse parte da realidade, vazio
// significaria "não sei".
//
// A agenda de avaliação é INTERNA e é a fonte da verdade (vide o cabeçalho de
// `grade.js`). Horário que existe na grade e está vazio É livre. O argumento
// caiu.
//
// O que sobrevive da VF-2 é a distinção que estava certa por outro motivo, e
// que aqui vira requisito por CÉLULA:
//
//   fechado    o dia inteiro não atende avaliação (com o motivo, quando há)
//   semGrade   este horário não existe NESTE dia — não é livre nem cheio
//   livre      existe e está vago
//
// É também por isso que o almoço continua não sendo um conceito: às 12:00 de
// terça a célula é `semGrade` porque a grade de terça não tem 12:00. Nenhuma
// regra nova, como na VF-1.

import { chaveDia, consomeVaga, gradeDoDia, horaDe } from '@/agenda/grade';
import { estadoVisual } from '@/agenda/confirmacao';

const ehRetorno = (a) => (a?.tipo ?? 'avaliacao') !== 'avaliacao';

/**
 * ★★★ A-3(a), 11/out/2026 — DE ONDE VEM O NOME DO SDR, e por que ele não vem do
 * agendamento.
 *
 * ⚠⚠ **Medido em produção (11/out): `agendamentos.sdrId` = 0 de 2.774.** O SDR
 * não está no documento do agendamento, e nunca esteve. Os dois caminhos foram
 * medidos em `docs/medicao-telas-estreia.md` (E5 e o "Ataque"):
 *
 *   (i)  denormalizar `sdrId`/`sdrNome` no agendamento — exige gravar no create
 *        E um backfill de 2.774 documentos, e cria dado duplicado que **divergirá**
 *        quando o lead trocar de SDR (a WT já consertou três divergências assim:
 *        `espaco`, `sdrNome`, `numeroPapel`);
 *   (ii) cruzar por `leadId` com os leads em MEMÓRIA.
 *
 * ★★★ Decisão do dono: **(ii)**, e **só para quem é de vendas**. O nome sai
 * sempre do lead, então não há o que divergir; e a recepção — que não lê `leads`
 * — continua abrindo a agenda sem carregar o funil.
 *
 * ★ `sdrPorLead` é um `Map` (ou qualquer coisa com `.get`), montado por quem
 * chama. ⚠ **Vazio é o caso normal, não falha:** para a recepção ele não existe,
 * e a caixinha simplesmente não mostra linha de SDR.
 */
const sdrDoLead = (leadId, sdrPorLead) => {
  if (!leadId || !sdrPorLead) return null;
  return sdrPorLead.get?.(leadId) ?? null;
};

/** O que a caixinha precisa saber, sem arrastar o documento inteiro. */
export function montarCaixa(a, sdrPorLead = null) {
  return {
    id: a?.id ?? '',
    leadId: a?.leadId ?? '',
    nome: a?.leadNome ?? '',
    telefone: a?.leadTelefone ?? '',
    /**
     * ★★ A-3(a) + ajuste 4 — DOIS CAMPOS, de propósito.
     *
     *   `sdr`          o nome CURTO ("Bruno", "Ana P.") — é o que cabe na
     *                  caixinha da semana;
     *   `sdrCompleto`  o nome como está no lead — vai no DETALHE (onde há
     *                  espaço) e no `title` da caixinha.
     *
     * ⚠ Um campo só obrigaria a escolher entre caber e informar. ★ Quem monta o
     * mapa decide o curto (precisa da equipe para desambiguar); aqui só se
     * transporta.
     * ★ Vazio é o caso normal: recepção (sem mapa) e lead sem SDR.
     */
    sdr: String(sdrDoLead(a?.leadId ?? '', sdrPorLead)?.curto ?? '').trim(),
    sdrCompleto: String(sdrDoLead(a?.leadId ?? '', sdrPorLead)?.completo ?? '').trim(),
    procedimento: a?.procedimento ?? '',
    profissional: a?.profissional ?? '',
    hora: horaDe(a?.dataHora),
    tipo: a?.tipo ?? 'avaliacao',
    status: a?.status ?? 'agendado',
    confirmadoEm: a?.confirmadoEm ?? null,
    estado: estadoVisual(a),
    retorno: ehRetorno(a),
    foraDaGrade: a?.foraDaGrade === true,
  };
}

/**
 * As linhas da visão: a UNIÃO dos horários dos dias exibidos, ordenada.
 *
 * ⚠ NUNCA 24 horas, e nunca os horários de um dia só. O sábado tem 12:00 e
 * 12:30, que o dia útil não tem; desenhar a semana com as linhas da segunda
 * esconderia duas vagas reais do sábado. A união com `semGrade` nas células que
 * não existem mostra as duas grades sem misturá-las.
 */
export function linhasDaVisao(config, dias = []) {
  const todas = new Set();
  for (const d of dias ?? []) {
    const { horarios, bloqueadas } = gradeDoDia(config, d);
    for (const h of horarios) todas.add(h.hora);
    // ★ A hora BLOQUEADA também é linha. Ela some de `horarios` (não tem vaga
    // hoje), mas existe na grade da clínica — sem linha, o bloqueio ficaria
    // invisível justamente no dia em que importa.
    for (const b of bloqueadas ?? []) todas.add(b.hora);
  }
  return [...todas].sort((a, b) => a.localeCompare(b));
}

/**
 * A grade inteira: linhas comuns, uma coluna por dia.
 *
 * ★ TRÊS COISAS NÃO PODEM SUMIR, e nenhuma delas entra nas células de vaga:
 *
 *   retorno          tem hora real e NÃO consome vaga (VF-1). Aparece na célula
 *                    da sua hora, marcado, e fora da contagem de livres.
 *   fora da grade    o escape criado na VF-1 para o caso real que não cabe.
 *                    Escondê-lo agora o tornaria inútil.
 *   cancelado        libera a vaga — então a célula tem de dizer "livre". Pôr a
 *                    caixinha ali contradiria isso. Vai para o rodapé do dia.
 *
 * Sem esse tratamento a grade seria uma REGRESSÃO contra a lista de hoje, que
 * mostra tudo o que existe no dia.
 */
export function montarGrade(config, dias = [], agendamentos = [], sdrPorLead = null) {
  const linhas = linhasDaVisao(config, dias);
  // ★ A-3(a) — uma só travessia por caixinha; `montarCaixa` recebe o mapa.
  const caixa = (a) => montarCaixa(a, sdrPorLead);

  const colunas = (dias ?? []).map((data) => {
    const chave = chaveDia(data);
    const { horarios, motivo, bloqueadas, excecao } = gradeDoDia(config, data);
    const doDia = (agendamentos ?? []).filter((a) => chaveDia(a?.dataHora) === chave);

    const cancelados = doDia.filter((a) => (a?.status ?? 'agendado') === 'cancelado');
    const vivos = doDia.filter((a) => (a?.status ?? 'agendado') !== 'cancelado');
    const porBloqueio = new Map((bloqueadas ?? []).map((b) => [b.hora, b]));
    // ★ A hora bloqueada CONTA como hora do dia: quem já tinha consulta marcada
    // nela continua tendo célula própria. Empurrá-la para "fora da grade"
    // esconderia o conflito que a clínica precisa resolver.
    const horasDoDia = new Set([...horarios.map((h) => h.hora), ...porBloqueio.keys()]);

    // Quem não tem linha onde morar: avaliação fora da grade e retorno em hora
    // que este dia não oferece.
    const fora = vivos.filter((a) => !horasDoDia.has(horaDe(a?.dataHora)));

    const celulas = linhas.map((hora) => {
      const definicao = horarios.find((h) => h.hora === hora) ?? null;
      if (!definicao) {
        const bloqueio = porBloqueio.get(hora) ?? null;
        // ★ QUARTO ESTADO. "Bloqueado hoje, por isto" NÃO é "sem vaga
        // configurada" (que significa "este horário nunca existiu aqui").
        // Confundir os dois faria a tela mentir sobre a grade da clínica e
        // perderia o motivo — o mesmo defeito do dia fechado sem explicação.
        if (bloqueio) {
          return {
            hora, semGrade: false, bloqueado: true, motivo: bloqueio.motivo,
            vagas: 0, ocupadas: 0, livres: 0, cheio: false,
            caixas: vivos.filter((a) => horaDe(a?.dataHora) === hora).map(caixa),
          };
        }
        return {
          hora, semGrade: true, bloqueado: false, motivo: '',
          vagas: 0, ocupadas: 0, livres: 0, cheio: false, caixas: [],
        };
      }
      const aqui = vivos.filter((a) => horaDe(a?.dataHora) === hora);
      // Só avaliação ocupa. O retorno aparece na célula sem descontar vaga.
      const ocupadas = aqui.filter(consomeVaga).length;
      return {
        hora,
        semGrade: false,
        bloqueado: false,
        motivo: '',
        vagas: definicao.vagas,
        ocupadas,
        livres: Math.max(0, definicao.vagas - ocupadas),
        cheio: ocupadas >= definicao.vagas,
        caixas: aqui.map(caixa),
      };
    });

    const comGrade = celulas.filter((c) => !c.semGrade && !c.bloqueado);
    return {
      chave,
      data,
      // Dia sem NENHUM horário: a clínica não atende avaliação nele. NÃO é cheio.
      fechado: horarios.length === 0,
      /**
       * ★★★ A-3 (12/out/2026) — A COLUNA PODE ENCOLHER? Decisão do dono.
       *
       * ★ O domingo ocupa 1/7 da semana para dizer "fechado". Encolhê-lo devolve
       * esse espaço aos dias que têm consulta — e é aí que o nome do paciente
       * estava sendo cortado.
       *
       * ★★★ TRÊS CONDIÇÕES, e cada uma tem motivo:
       *
       *   `horarios.length === 0`  o dia não atende avaliação;
       *   `!excecao`               ⚠⚠ fechado pelo **horário da clínica**, não por
       *                            exceção da data. **FERIADO CONTINUA LARGO** — é
       *                            decisão do dono, e faz sentido: feriado é
       *                            notícia, domingo não. `gradeDoDia` devolve
       *                            `excecao: true` exatamente nesse caso;
       *   `doDia.length === 0`     ⚠⚠⚠ **se há consulta, a largura é normal.**
       *                            Consulta em dia fechado existe (a VF-1 criou o
       *                            "fora da grade" para isso), e esconder-la numa
       *                            coluna de 68px seria perder o conflito que a
       *                            clínica precisa ver.
       *
       * ⚠ `doDia` inclui CANCELADAS de propósito: coluna com cancelamento tem o
       * que mostrar no rodapé.
       */
      estreitavel: horarios.length === 0 && !excecao && doDia.length === 0,
      motivo,
      vagas: comGrade.reduce((t, c) => t + c.vagas, 0),
      livres: comGrade.reduce((t, c) => t + c.livres, 0),
      celulas,
      fora: fora.map(caixa),
      cancelados: cancelados.map(caixa),
    };
  });

  return { linhas, colunas };
}
